import { Injectable } from '@angular/core';
import {Item} from '../shared/models/domain/item';
import {
    Characteristic,
    Detail,
    FromAirport,
    Geoplace,
    SaleDriver,
    ToAirport,
    Tooltip
} from '../shared/models/favorites/favorite';
import {TranslateService} from '@ngx-translate/core';
import * as moment from 'moment';
import {AppSettingsService} from './app-settings/app-settings.service';
import {CalculationTextPipe} from '../pipes/calculations-text.pipe';
import {URLParamsConstants} from '../utils/constants/UrlConstants';
import {CurrencyService} from './currency/currency.service';

@Injectable({
    providedIn: 'root'
})
export class ProductUtilsService {

    private readonly _transfer_waiting_time = 'transfer-cluster.waiting.time';
    private readonly _transfer_duration = 'transfer-cluster.duration';
    private readonly _transfer_calculation = 'transfer-cluster.calculation';
    private readonly _transfer_free_cancellation = 'transfer-marquee.freeCancellation';
    private readonly _transfer_free_cancellation_tooltip_description = 'transfer-marquee.freeCancellation.tooltip.description';
    private readonly _transfer_free_cancellation_tooltip_title = 'transfer-marquee.freeCancellation.tooltip.title';
    private readonly _transfer_meetgreet = 'transfer-marquee.meetgreet';
    private readonly _amenities_maximum_passengers = 'cluster-amenities.maximum.passengers';
    private readonly _amenities_suitcase_plural = 'cluster-amenities.suitcase.plural';
    private readonly _amenities_suitcase_singular = 'cluster-amenities.suitcase.singular';
    private readonly _amenities_carryOn_plural = 'cluster-amenities.carryOn.plural';
    private readonly _amenities_carryOn_singular = 'cluster-amenities.carryOn.singular';
    private readonly _amenities_handbag_plural = 'cluster-amenities.handbag.plural';
    private readonly _amenities_handbag_singular = 'cluster-amenities.handbag.singular';
    private readonly _shared_per_vehicle = 'shared.per.vehicle';
    private readonly _shared_per_passenger = 'shared.per.passenger';
    private readonly _flexible_cancellation_policy = 'flexible_cancellation_policy';
    private readonly _person = 'PERSON';

    constructor(private translateService: TranslateService,
                private appSettingsService: AppSettingsService,
                private currencyService: CurrencyService,
                private calculationTextPipe: CalculationTextPipe) {}

    getSaleDriver(item: Partial<Item>): SaleDriver[] {
        const saleDrivers: SaleDriver[] = [];
        let saleDriver: SaleDriver = { text: '', eva_class: '' };
        const tooltip: Tooltip = {};

        if (item.tags !== null && item.tags.includes(this._flexible_cancellation_policy)) {
            saleDriver.eva_class = '-info -j1 -flexible';
            saleDriver.icon = 'info-circle-outline';
            saleDriver.text = this.translateService.instant(this._transfer_free_cancellation);
            tooltip.text = this.translateService.instant(this._transfer_free_cancellation_tooltip_description);
            tooltip.title = this.translateService.instant(this._transfer_free_cancellation_tooltip_title);
            saleDriver.tooltip = tooltip;
            saleDrivers.push(saleDriver);
            saleDriver = { text: '', eva_class: '' };
        }

        if (item.meetAndGreet) {
            saleDriver.eva_class = '-info -j4';
            saleDriver.text = this.translateService.instant(this._transfer_meetgreet);
            saleDrivers.push(saleDriver);
        }

        return saleDrivers;
    }

    getToAirport(item: Partial<Item>): ToAirport {
        const toAirport: ToAirport = {};
        const geoplace: Geoplace = {
            id: '',
        };

        toAirport.date = (item.returnSegment?.pickupDatetime)? this.formatDateToISOWithoutTimezone(item.returnSegment.pickupDatetime.toString()):null;
        toAirport.airport = item.returnSegment.destination.code;
        geoplace.id = item.returnSegment.origin.code;
        geoplace.text = item.returnSegment.origin.name;
        toAirport.geoplace = geoplace;

        return toAirport;
    }

    getFromAirport(item: Partial<Item>): FromAirport {
        const fromAirport: FromAirport = {
            date: '',
            airport: '',
            geoplace: undefined,
        };
        const geoplace: Geoplace = {
            id: '',
        };

        fromAirport.date = (item.outwardSegment?.pickupDatetime)?this.formatDateToISOWithoutTimezone(item.outwardSegment.pickupDatetime?.toString()):null;
        fromAirport.airport = item.outwardSegment.origin.code;
        geoplace.id = item.outwardSegment.destination.code;
        geoplace.text = item.outwardSegment.destination.name;
        fromAirport.geoplace = geoplace;
        return fromAirport;
    }

    formatDateToISOWithoutTimezone(dateString: string): string {
        return new Date(dateString).toISOString().replace(/\.\d{3}Z$/, '');
    }

    getDetails(item: Partial<Item>): Detail[] {
        const details: Detail[] = [];

        if (item.outwardSegment) {
            this.addDetail(
                details,
                'time',
                `${this.translateService.instant(this._transfer_duration)}: ${item.outwardSegment.duration}`
            );
            this.addDetail(
                details,
                'airplane-going',
                this.calculationTextPipe.transform(
                    this._transfer_calculation,
                    item.coverage,
                    item.outwardSegment.destination.type
                )
            );

            if (item.outwardSegment.pickupDatetime) {
                this.addDetail(details, null, this.getTextOutward(item));
            }
        }

        if (item.returnSegment && item.returnSegment.pickupDatetime) {
            this.addDetail(details, null, this.getTextReturn(item));
        }

        return details;
    }

    private addDetail(details: Detail[], icon: string | null, text: string): void {
        details.push({ icon, text });
    }

    private getTextOutward(item: Partial<Item>): string {
        let text = `${item.outwardSegment.pickupText}: ${this.getTransferDate(item.outwardSegment.pickupDatetime)}`;
        if (item.outwardSegment.supplierMaximumWaitingTime) {
            text += ` ${this.translateService.instant(this._transfer_waiting_time)} ${
                item.outwardSegment.supplierMaximumWaitingTime
            }`;
        }
        return text;
    }

    private getTextReturn(item: Partial<Item>): string {
        let text = `${item.returnSegment.pickupText}: ${this.getTransferDate(item.returnSegment.pickupDatetime)}`;
        if (item.returnSegment.supplierMaximumWaitingTime) {
            text += ` ${this.translateService.instant(this._transfer_waiting_time)} ${
                item.returnSegment.supplierMaximumWaitingTime
            }`;
        }
        return text;
    }

    private getTransferDate(date: Date): string {
        moment.locale(this.appSettingsService.getLocale());
        const noUTCDate = String(date).slice(0, -6);
        const newDate = moment(noUTCDate);

        return `${newDate.format('D MMM')} - ${newDate.format('HH:mm')}`;
    }

    getUrl(item: Partial<Item>): string {
        const currentPathname = window.location.pathname;

        const params = new URLSearchParams({
            clickedPrice: `${this.currencyService.selectedCurrency.value}_${
                item.prices[this.currencyService.selectedCurrency.value].totalPrice
            }`,
            refererEvent: URLParamsConstants.FAV,
            serviceId: item.serviceId,
        });

        return `${currentPathname}?${params.toString()}`;
    }

    getCharacteristics(item: Partial<Item>): Characteristic[] {
        const characteristics: Characteristic[] = [];

        const createCharacteristic = (text: string, icon: string, tooltipText: string): Characteristic => ({
            text,
            icon,
            tooltip: { text: tooltipText },
        });

        characteristics.push(
            createCharacteristic(
                item.maximumCapacity.toString(),
                'user',
                `${this.translateService.instant(this._amenities_maximum_passengers, {
                    number: item.maximumCapacity,
                })} ${this.translateService.instant(this._shared_per_vehicle)}`
            )
        );

        if (item.baggageDetails.suitcaseRestriction) {
            characteristics.push(
                createCharacteristic(
                    item.baggageDetails.suitcaseRestriction.maximumAllowed.toString(),
                    'bag',
                    item.baggageDetails.suitcaseRestriction.text
                )
            );
        }

        if (item.baggageDetails.handbagRestriction) {
            characteristics.push(
                createCharacteristic(
                    item.baggageDetails.handbagRestriction.maximumAllowed.toString(),
                    'handbag',
                    item.baggageDetails.handbagRestriction.text
                )
            );
        }

        if (item.baggageDetails.carryOnRestriction) {
            characteristics.push(
                createCharacteristic(
                    item.baggageDetails.carryOnRestriction.maximumAllowed.toString(),
                    'carry-on',
                    item.baggageDetails.carryOnRestriction.text
                )
            );
        }

        return characteristics;
    }
}
